// attempt-baseline-inventory.ts
//
// Before/after snapshot of a mission worktree used by result-verifier to
// compute the exact set of paths an attempt touched (§5.4, phase 06 Agent B
// brief). Two pieces:
//   1. Tree SHA of HEAD (`git rev-parse HEAD^{tree}`) — cheap "did HEAD's
//      tree change" signal (also used to catch an unexpected commit).
//   2. Per-file sha256 inventory (path -> hash) covering TRACKED +
//      UNTRACKED-but-not-ignored files, so an attempt that edits an existing
//      file OR creates a brand new file is visible in the diff either way.
//
// `git ls-files --cached --others --exclude-standard` gives exactly
// "tracked ∪ untracked-not-ignored" in one call, matching git's own
// ignore-file resolution instead of re-implementing .gitignore parsing.
import { createHash } from "node:crypto";
import { promises as fs } from "node:fs";
import { join } from "node:path";
import { spawnAsyncCapture } from "../spawn-async-capture-with-timeout.js";

export interface AttemptBaselineInventory {
  treeSha: string;
  /** `git rev-parse HEAD` at capture time — precise "did HEAD move" signal, independent of tree content (an `--allow-empty` commit changes headSha but not treeSha). */
  headSha: string;
  files: Record<string, string>;
}

export interface InventoryDiff {
  added: string[];
  modified: string[];
  deleted: string[];
}

async function runGit(args: string[], cwd: string): Promise<string> {
  const res = await spawnAsyncCapture("git", args, {
    cwd,
    timeoutMs: 30_000,
    forwardSigint: false,
  });
  if (res.status !== 0) {
    throw new Error(`attempt-baseline-inventory: git ${args.join(" ")} thất bại: ${res.stderr}`);
  }
  return res.stdout;
}

/**
 * Review 06f M1: never follow a symlink. `fs.readFile` reads THROUGH a link,
 * so a worker that swapped `.avatar/PLAN.md` (or a whole directory such as
 * `.avatar/tickets`) for a symlink to a byte-identical copy outside the
 * worktree produced an empty diff. A link hashes as `symlink:<target>` (git's
 * own view: it stores the target, not the bytes) and a path below a symlinked
 * directory as `symlink-parent:<dir>-><target>`; neither is a 64-hex sha256,
 * so either differs from a regular file's hash. Regular files hash exactly as
 * before (sha256 of the bytes), so baselines captured earlier still compare.
 */
async function hashPath(
  repoDir: string,
  relPath: string,
  linkOf: (relDir: string) => Promise<string | undefined>,
): Promise<string | undefined> {
  try {
    const parts = relPath.split("/");
    for (let i = 1; i < parts.length; i++) {
      const dir = parts.slice(0, i).join("/");
      const target = await linkOf(dir);
      if (target !== undefined) return `symlink-parent:${dir}->${target}`;
    }
    const abs = join(repoDir, relPath);
    if ((await fs.lstat(abs)).isSymbolicLink()) return `symlink:${await fs.readlink(abs)}`;
    const buf = await fs.readFile(abs);
    return createHash("sha256").update(buf).digest("hex");
  } catch {
    // File listed by git but unreadable at snapshot time (e.g. removed
    // between ls-files and read) — treat as absent rather than throwing,
    // the diff will pick this up as a deletion.
    return undefined;
  }
}

/** Per-snapshot memo: `readlink` target of a repo-relative directory component, or undefined if it is not a symlink. */
function directoryLinkLookup(repoDir: string): (relDir: string) => Promise<string | undefined> {
  const memo = new Map<string, Promise<string | undefined>>();
  return (relDir) => {
    let hit = memo.get(relDir);
    if (hit === undefined) {
      const abs = join(repoDir, relDir);
      hit = fs
        .lstat(abs)
        .then((st) => (st.isSymbolicLink() ? fs.readlink(abs) : undefined))
        .catch(() => undefined);
      memo.set(relDir, hit);
    }
    return hit;
  };
}

/**
 * Snapshot the worktree at `repoDir`: HEAD's tree SHA + sha256 of every
 * tracked-or-untracked-not-ignored file. `HEAD^{tree}` requires at least one
 * commit to exist (mission worktrees always have the bootstrap commit by the
 * time an attempt starts).
 */
export async function captureBaselineInventory(repoDir: string): Promise<AttemptBaselineInventory> {
  const treeSha = (await runGit(["rev-parse", "HEAD^{tree}"], repoDir)).trim();
  const headSha = (await runGit(["rev-parse", "HEAD"], repoDir)).trim();
  const listing = await runGit(
    ["ls-files", "--cached", "--others", "--exclude-standard", "-z"],
    repoDir,
  );
  const paths = listing.split("\0").filter((p) => p.length > 0);

  const files: Record<string, string> = {};
  const linkOf = directoryLinkLookup(repoDir);
  for (const relPath of paths) {
    const hash = await hashPath(repoDir, relPath, linkOf);
    if (hash !== undefined) files[relPath] = hash;
  }

  return { treeSha, headSha, files };
}

/**
 * Pure diff of two inventories: added (in `after` not `before`), modified
 * (present in both, hash differs), deleted (in `before` not `after`).
 */
export function diffInventory(
  before: AttemptBaselineInventory,
  after: AttemptBaselineInventory,
): InventoryDiff {
  const added: string[] = [];
  const modified: string[] = [];
  const deleted: string[] = [];

  for (const path of Object.keys(after.files)) {
    if (!(path in before.files)) {
      added.push(path);
    } else if (before.files[path] !== after.files[path]) {
      modified.push(path);
    }
  }
  for (const path of Object.keys(before.files)) {
    if (!(path in after.files)) deleted.push(path);
  }

  return { added, modified, deleted };
}
